import { useState, useRef, ReactNode } from 'react';
import { Button } from '@/components/button';
import styles from './dropdown-input.module.css';
import clsx from 'clsx';
import { MaterialSymbolIcon, MaterialSymbolIconProps } from '@/components/material-symbol-link';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useOutsideClick } from '@/hooks/use-outside-click';

interface DropdownInputProps {
  text: ReactNode;
  children: React.ReactNode;
  position?: 'left' | 'right';
  isFloating?: boolean;
  className?: string;
  iconProps?: MaterialSymbolIconProps;
}

export function DropdownInput({
  text,
  children,
  position = 'left',
  className,
  iconProps,
  isFloating = true,
}: DropdownInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleBlur = (event: React.FocusEvent) => {
    if (containerRef.current && !containerRef.current.contains(event.relatedTarget as Node)) {
      setIsOpen(false);
    }
  };

  const handleMobileClick = (event: React.MouseEvent) => {
    if (contentRef.current && contentRef.current.contains(event.target as Node)) {
      return;
    }
    setIsOpen(!isOpen);
  };

  useOutsideClick(containerRef, () => setIsOpen(false));

  const isMobile = useMediaQuery('--screen-md');

  const buttonProps = !isMobile
    ? {
        onMouseEnter: () => setIsOpen(true),
        onMouseLeave: () => setIsOpen(false),
        onFocus: () => setIsOpen(true),
        onBlur: handleBlur,
      }
    : {
        onClick: handleMobileClick,
      };

  return (
    <Button ref={containerRef} variant="none" className={styles.dropdownContainer} {...buttonProps}>
      <span className={clsx(styles.dropdownButton, isOpen && styles.open, className, 'body-3')}>
        {iconProps && (
          <MaterialSymbolIcon {...iconProps} className={clsx(styles.icon, iconProps.className)} />
        )}
        {text}
        <MaterialSymbolIcon name="keyboard_arrow_down" />
      </span>
      {isOpen && (
        <div
          ref={contentRef}
          className={clsx(
            styles.dropdownContent,
            styles[position],
            'body-3',
            isFloating && styles.floating,
          )}
        >
          {children}
        </div>
      )}
    </Button>
  );
}
